import React, { useState, useEffect } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { useSelector, useDispatch } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { FullPageCard } from "component/shared/full-page-card";
import { styles } from "./style";
import { User } from "model/user";
import { saveUser } from "redux/slice/authentication";
import { updateAgents } from "redux/slice/agents";
import { RootState } from "redux/store";
import { setEnhancedBioEnabled, setBioPageLayout } from "redux/slice/broker-settings";
import MuiAlert, { AlertColor as Color } from "@mui/material/Alert";
import { PhotoUploadDialogButton } from "component/shared/photo-upload-dialog-button";
import { AgentSortOrderType } from "type/agent-sort-order";
import { BioPageLayoutType } from "type/bio-page-layout";
import { Agent } from "model/agent";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { CardLegacy as Card } from "./card/legacy";
import { deepCompare } from "shared/deep-compare";
import { getAgentsByCustomSort } from "redux/selector";
import bioLayoutHorizontal from "image/bio-layout-horizontal.png";
import bioLayoutVertical from "image/bio-layout-vertical.png";

interface Props extends PageProps, WithStyles<typeof styles>, Router.RouteComponentProps {
	user: User;
}

const Component: React.FC<Props> = ({ user, classes = {}, history }) => {
	const dispatch = useDispatch<any>();
	const authLoading = useSelector((state: RootState) => state.authentication.loading);
	const authError = useSelector((state: RootState) => state.authentication.error);
	const agents = useSelector(getAgentsByCustomSort);

	const [sendAgentsLeadTextNotifications, setSendAgentsLeadTextNotifications] = useState(
		user.sendAgentsLeadTextNotifications || false
	);
	const [hideAgentSelector, setHideAgentSelector] = useState(user.hideAgentSelector || false);

	const [isEnhancedBioEnabled, setIsEnhancedBioEnabled] = useState(user.isEnhancedBioEnabled ?? false);
	const [bioPageLayout, setBioPageLayoutState] = useState(BioPageLayoutType.getByIdOrDefault(user.bioPageLayout === 2 ? "layout2" : "layout1"));
	const [saving, setSaving] = useState(false);
	const [logoImageSource, setLogoImageSource] = useState<"upload" | "url">("url");
	const [logoUrl, setLogoUrl] = useState(user.controlPanelUrl || "");
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [snackBarMessage, setSnackBarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<Color | undefined>(undefined);
	const [agentSortOrder, setAgentSortOrder] = useState(user.agentSortOrder);
	const [agentOrder, setAgentOrder] = useState<Agent[]>([...agents]);

	useEffect(() => {
		if (!deepCompare(agents, agentOrder)) {
			setAgentOrder([...agents]);
		}
	}, [agents]);

	const onDragEnd = (result: any) => {
		const { destination, source } = result;
		if (!destination) return;
		if (destination.droppableId === source.droppableId && destination.index === source.index) {
			return;
		}

		const items = Array.from(agentOrder);
		const [reordered] = items.splice(source.index, 1);
		items.splice(destination.index, 0, reordered);
		setAgentOrder(items);
	};

	const save = () => {
		const updateUser = {
			...user,
			sendAgentsLeadTextNotifications,
			hideAgentSelector,
			controlPanelUrl: logoUrl,
			agentSortOrder,
			isEnhancedBioEnabled,
			bioPageLayout: bioPageLayout.id === "layout2" ? 2 : 1,
		};
		dispatch(saveUser({ user: updateUser }));

		const agentsToUpdate = agentOrder.map((agent) => {
			const { ...agentData } = agent;
			agentData.sortOrder = undefined;
			return agentData;
		});
		dispatch(updateAgents({ agents: agentsToUpdate }));

		setSaving(true);
	};

	const title = "Agent/Office Basic Settings";

	return (
		<DashboardLayout
			permitted={user && user.permissions.broker}
			title={title}
			header={
				<Mui.Typography variant="h1">
					<FeatherIcon>
						<Icons.Settings />
					</FeatherIcon>
					{title}
				</Mui.Typography>
			}
		>
			<FullPageCard>
				<Mui.Grid
					container
					direction="column"
					justifyContent="center"
					alignItems="stretch"
					spacing={2}
					lg={8}
					xl={6}
				>
					{/* Agent Bio Pages Section - Only for kestrel (v10) accounts */}
					{process.env.REACT_APP_ENHANCED_BIO_ENABLED === 'true' && user.kestrelVersion.all && user.permissions.enhancedAgentBioPages && (
						<Mui.Grid item>
							<Mui.Box mb={3}>
								<Mui.Typography variant="body1" className={classes.bioPagesSectionTitle}>
									Agent Bio Pages
								</Mui.Typography>

								{/* Enhanced Bio Toggle */}
								<Mui.Box mb={3}>
									<Mui.FormControlLabel
										control={
											<Mui.Switch
												checked={isEnhancedBioEnabled}
												onChange={(e) => { setIsEnhancedBioEnabled(e.target.checked); dispatch(setEnhancedBioEnabled(e.target.checked)); }}
												color="secondary"
											/>
										}
										label={<span className={classes.enhancedBioLabel}>Use Enhanced Bio Pages</span>}
									/>
								</Mui.Box>

								{/* Layout Selection */}
								{isEnhancedBioEnabled && (
									<Mui.Box>
										<Mui.Typography variant="body1" className={classes.layoutSelectionTitle}>
											Choose a Default Bio Page Layout
										</Mui.Typography>
										<Mui.Typography
											variant="body2"
											color="textSecondary"
											className={classes.layoutSelectionDescription}
										>
											Layout applies to all agent bio pages on your site.
										</Mui.Typography>

										<Mui.RadioGroup
											value={bioPageLayout.id}
											onChange={(e) => { setBioPageLayoutState(BioPageLayoutType.getByIdOrDefault(e.target.value)); dispatch(setBioPageLayout(e.target.value)); }}
										>
										<Mui.Box className={classes.layoutOptionsContainer}>
											{/* Layout 1 - Horizontal */}
												<Mui.Box
													onClick={() => { setBioPageLayoutState(BioPageLayoutType.HORIZONTAL); dispatch(setBioPageLayout(BioPageLayoutType.HORIZONTAL.id)); }}
													className={classes.layoutOption}
												>
													<Mui.Radio
														checked={bioPageLayout === BioPageLayoutType.HORIZONTAL}
														value={BioPageLayoutType.HORIZONTAL.id}
														className={classes.layoutRadio}
													/>
													<img
														src={bioLayoutHorizontal}
														alt="Horizontal layout"
														className={classes.layoutImage}
													/>
												</Mui.Box>

											{/* Layout 2 - Vertical */}
												<Mui.Box
													onClick={() => { setBioPageLayoutState(BioPageLayoutType.VERTICAL); dispatch(setBioPageLayout(BioPageLayoutType.VERTICAL.id)); }}
													className={classes.layoutOption}
												>
													<Mui.Radio
														checked={bioPageLayout === BioPageLayoutType.VERTICAL}
														value={BioPageLayoutType.VERTICAL.id}
														className={classes.layoutRadio}
													/>
													<img
														src={bioLayoutVertical}
														alt="Vertical layout"
														className={classes.layoutImage}
													/>
												</Mui.Box>
										</Mui.Box>
									</Mui.RadioGroup>
								</Mui.Box>
								)}
							</Mui.Box>
							<Mui.Divider />
						</Mui.Grid>
					)}

					{/* Team Features - Using Switches per deworsham's changes */}
					{user.permissions.showTeamFeatures && (
						<>
							<Mui.Grid item>
								<Mui.FormControlLabel
									control={
										<Mui.Switch
											checked={sendAgentsLeadTextNotifications}
											onChange={() =>
												setSendAgentsLeadTextNotifications(!sendAgentsLeadTextNotifications)
											}
											name="sendAgentsLeadTextNotifications"
											color="secondary"
										/>
									}
									label="Send Agents Lead Notifications via Text"
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.FormControlLabel
									control={
										<Mui.Switch
											checked={hideAgentSelector}
											onChange={() => setHideAgentSelector(!hideAgentSelector)}
											name="hideAgentSelector"
											color="secondary"
										/>
									}
									label="Hide the Agent Selector on Lead Capture Forms"
								/>
							</Mui.Grid>
						</>
					)}
					<Mui.Grid item>
						<Mui.FormControl component="fieldset" fullWidth>
							<Mui.Typography style={{ fontSize: 18, fontWeight: 700, lineHeight: "24px", color: "#1C1B1F", marginBottom: 8 }}>Control Panel Logo</Mui.Typography>
							<Mui.RadioGroup
								value={logoImageSource}
								onChange={(e) => setLogoImageSource(e.target.value as "upload" | "url")}
							>
								<Mui.FormControlLabel value="url" control={<Mui.Radio color="secondary" />} label="Image URL" />
								<Mui.FormControlLabel value="upload" control={<Mui.Radio color="secondary" />} label="Upload Image" />
							</Mui.RadioGroup>
							{logoImageSource === "url" && (
								<Mui.TextField
									fullWidth
									inputProps={{ maxLength: 100 }}
									label="Enter Image URL"
									onChange={(event) => setLogoUrl(event.target.value)}
									placeholder="Enter Image URL (include http://)"
									value={logoUrl}
									variant="outlined"
								/>
							)}
							{logoImageSource === "upload" && (
								<Mui.Box display="inline-block">
								<PhotoUploadDialogButton
									multiple={false}
									callback={(uploads) => {
										const upload = uploads.find((upload) => upload.url);
										if (upload && upload.url) {
											setLogoImageSource("url");
											setLogoUrl(upload ? upload.url : "");
											setOpenSnackbar(true);
											setSnackBarMessage("Upload Successful");
											setSnackbarSeverity("success");
										} else {
											setOpenSnackbar(true);
											setSnackBarMessage("Error");
											setSnackbarSeverity("error");
										}
									}}
									label="Upload Image"
									color="secondary"
									style={{ marginBottom: 4, marginTop: 8, }}
									resourceType="image"
								/>
								</Mui.Box>
							)}
						</Mui.FormControl>
					</Mui.Grid>
					{user.permissions.showTeamFeatures && user.kestrelVersion.all && (
						<Mui.Grid item>
							<Mui.FormControl component="fieldset">
								<Mui.Typography style={{ fontSize: 18, fontWeight: 700, lineHeight: "24px", color: "#1C1B1F", marginBottom: 8 }}>Agent Display Order on Team Page</Mui.Typography>
								<Mui.RadioGroup
									value={agentSortOrder.id}
									onChange={(e) => setAgentSortOrder(AgentSortOrderType.getByIdOrDefault(e.target.value))}
								>
									{AgentSortOrderType.values().map((type) => (
										<Mui.FormControlLabel
											key={type.id}
											value={type.id}
											control={<Mui.Radio color="secondary" />}
											label={type.label}
										/>
									))}
								</Mui.RadioGroup>
							</Mui.FormControl>
						</Mui.Grid>
					)}
					{user.permissions.showTeamFeatures && agentSortOrder.id === AgentSortOrderType.CUSTOM.id && (
						<Mui.Grid item>
							<Mui.Typography variant="h6">Agents</Mui.Typography>
							<Mui.Typography variant="body2" color="textSecondary">
								Drag to reorder the agent list
							</Mui.Typography>
							<DragDropContext onDragEnd={onDragEnd}>
								<Droppable droppableId="droppable">
									{(provided) => (
										<div {...provided.droppableProps} ref={provided.innerRef}>
											{agentOrder.map((agent, index) => (
												<Draggable
													key={agent.id}
													draggableId={String(agent.id)}
													index={index}
												>
													{(provided) => (
														<div
															ref={provided.innerRef}
															{...provided.draggableProps}
															{...provided.dragHandleProps}
														>
															<Card
																agent={agent}
																position={index + 1}
																onDelete={() => {
																	const results = [...agentOrder];
																	results.splice(index, 1);
																	setAgentOrder(results);
																}}
															/>
														</div>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</div>
									)}
								</Droppable>
							</DragDropContext>
						</Mui.Grid>
					)}
					<Mui.Grid item>
						<Mui.Grid container alignItems="center" spacing={2}>
							<Mui.Grid item>
								<Mui.Button
									onClick={() => setSaving(false)}
									variant="outlined"
									color="primary"
								>
									Cancel
								</Mui.Button>
								<Mui.Button
									onClick={save}
									disabled={saving && authLoading}
									variant="contained"
									color="primary"
									style={{ marginLeft: 8 }}
								>
									Save Changes
								</Mui.Button>
							</Mui.Grid>
						</Mui.Grid>
						<Mui.Snackbar
							open={saving && !authLoading && !authError}
							message="Settings saved"
							autoHideDuration={6000}
							onClose={() => setSaving(false)}
							action={
								<Mui.IconButton
									size="small"
									aria-label="close"
									color="inherit"
									onClick={() => setSaving(false)}
								>
									<Icons.X fontSize="small" />
								</Mui.IconButton>
							}
						>
							<MuiAlert severity="info">
								<Mui.Typography>Settings saved</Mui.Typography>
							</MuiAlert>
						</Mui.Snackbar>
					</Mui.Grid>
				</Mui.Grid>
			</FullPageCard>
		</DashboardLayout>
	);
};

export const SettingsBrokerPageLegacy = withStyles(Router.withRouter(Component), styles);
